import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import PandaWatermark from '@/assets/icons/cover-images/panda-watermark.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { WatermarkKind, resolveWatermarkTag } from '../shared';

const WATERMARK_TAG_CLASSNAMES: Record<WatermarkKind, string> = {
  'actual-photo': 'actual-photo-var8',
  'ai-generated': 'ai-tag-var8 ai-tag-var8--generated',
  'ai-assisted': 'ai-tag-var8 ai-tag-var8--assisted',
};

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var8');

            if (variation.topText) {
              const topText = document.createElement('div');
              topText.classList.add('top-text-var8');
              let text = variation.topText;
              if (variation.topTextBrakes) {
                topText.innerText = text;
              } else {
                topText.innerHTML = text;
              }
              imageHolder.appendChild(topText);
            }

            if (variation.bottomText) {
              const bottomText = document.createElement('div');
              const bottomTextP = document.createElement('p');
              bottomText.classList.add('bottom-text-var8');
              let text = `“${replaceQuotes(variation.bottomText)}”`;
              if (variation.bottomTextBrakes) {
                bottomTextP.innerText = text;
              } else {
                text = distributeTextCanvas(text, {
                  maxWidth: 590,
                  fontFamily: 'CustomArial',
                  minFontSize:
                    variation.fontSize !== 0 ? variation.fontSize! : 38,
                });
                bottomTextP.innerHTML = text;
              }
              bottomText.appendChild(bottomTextP);
              imageHolder.appendChild(bottomText);

              if (variation.fontSize) {
                bottomText.style.fontSize = `${variation.fontSize}px`;
              }
            }
            if (variation.watermark) {
              const pandaWatermark = new Image();
              pandaWatermark.classList.add('watermark-var8');
              pandaWatermark.src = PandaWatermark;
              imageHolder.appendChild(pandaWatermark);
            }

            const imageWrap = document.createElement('div');
            imageWrap.classList.add('image-wrap-var8');

            const fullImage = new Image();
            fullImage.src = variation.imageSrc;
            fullImage.classList.add('full-image-var8');
            imageWrap.appendChild(fullImage);

            const watermark = resolveWatermarkTag(variation.watermarkTag);
            if (watermark) {
              const tag = new Image();
              tag.classList.add(
                ...WATERMARK_TAG_CLASSNAMES[watermark.kind].split(' '),
              );
              tag.src = watermark.src;
              imageWrap.appendChild(tag);
            }

            imageHolder.appendChild(imageWrap);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var8',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      const validImages = await generateImagesInParallel(
        variations,
        generateImageVariations,
        (current) => setCounter(current),
      );

      setImages(validImages);
      setShowProgress(false);
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages);
      } else {
        await downloadImagesAsZip(images);
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
